import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from './client';
import type { Entity, PaginatedResponse, EntityType } from '../types';

interface EntitiesParams {
  page?: number;
  page_size?: number;
  type?: EntityType;
  search?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  min_risk_score?: number;
  tags?: string[];
}

function mapEntityFromAPI(raw: Record<string, unknown>): Entity {
  return {
    id: String(raw.id || ''),
    type: (raw.entity_type || raw.type || 'document') as EntityType,
    name: String(raw.name || ''),
    aliases: (raw.aliases as string[]) || [],
    description: String(raw.description || ''),
    properties: (raw.properties || {}) as Record<string, unknown>,
    risk_score: Number(raw.risk_score || 0),
    confidence: Number(raw.confidence || 1),
    source: String(raw.source || ''),
    tags: (raw.tags as string[]) || [],
    created_at: String(raw.created_at || new Date().toISOString()),
    updated_at: String(raw.updated_at || new Date().toISOString()),
    first_seen: String(raw.first_seen || raw.created_at || new Date().toISOString()),
    last_seen: String(raw.last_seen || raw.updated_at || new Date().toISOString()),
  };
}

async function fetchEntities(params: EntitiesParams): Promise<PaginatedResponse<Entity>> {
  // Backend uses `q` for search, `entity_type` for type filter
  const { search, type, ...rest } = params;
  const queryParams: Record<string, unknown> = { ...rest };
  if (search) queryParams.q = search;
  if (type) queryParams.entity_type = type;
  const { data } = await apiClient.get('/entities', { params: queryParams });
  const total = data.total || 0;
  const pageSize = data.page_size || 20;
  return {
    ...data,
    items: (data.items || []).map((e: Record<string, unknown>) => mapEntityFromAPI(e)),
    total,
    total_pages: Math.ceil(total / pageSize) || 1,
  };
}

async function fetchEntity(id: string): Promise<Entity> {
  const { data } = await apiClient.get(`/entities/${id}`);
  return mapEntityFromAPI(data);
}

async function fetchEntityNetwork(
  id: string,
  depth: number = 2
): Promise<{ nodes: Record<string, unknown>[]; edges: Record<string, unknown>[]; stats: Record<string, unknown> }> {
  const { data } = await apiClient.get(`/entities/${id}/network`, { params: { depth, max_nodes: 500 } });
  return data;
}

async function createEntity(entity: Partial<Entity>): Promise<Entity> {
  const { data } = await apiClient.post('/entities', entity);
  return data;
}

async function updateEntity({ id, ...updates }: Partial<Entity> & { id: string }): Promise<Entity> {
  const { data } = await apiClient.put(`/entities/${id}`, updates);
  return data;
}

async function deleteEntity(id: string): Promise<void> {
  await apiClient.delete(`/entities/${id}`);
}

async function searchEntities(query: string): Promise<Entity[]> {
  // Backend uses POST /entities/search with body
  const { data } = await apiClient.post('/entities/search', { query, page_size: 50 });
  return data.items || data;
}

interface ScanEntityPayload {
  name: string;
  identity: Record<string, unknown>;
  social_profiles: Record<string, unknown>;
  digital_life: Record<string, unknown>;
  enrichment: Record<string, unknown>;
  entourage: Record<string, unknown>;
  photos: string[];
  best_guess: string;
  source: string;
}

interface ScanEntityResult {
  main_entity: Entity;
  related_entities: Entity[];
  relationships: Array<{ id: string; source_id: string; target_id: string; relationship_type: string }>;
  total_entities: number;
  total_relationships: number;
}

async function createEntityFromScan(payload: ScanEntityPayload): Promise<ScanEntityResult> {
  const { data } = await apiClient.post('/entities/from-scan', payload);
  return data;
}

async function createRelationship(payload: {
  source_id: string;
  target_id: string;
  relation_type: string;
  properties?: Record<string, unknown>;
  confidence?: number;
}): Promise<unknown> {
  const { data } = await apiClient.post('/entities/relationships', payload);
  return data;
}

// ── React Query Hooks ──

export function useEntities(params: EntitiesParams = {}) {
  return useQuery({
    queryKey: ['entities', params],
    queryFn: () => fetchEntities(params),
    staleTime: 30_000,
  });
}

export function useEntity(id: string | null) {
  return useQuery({
    queryKey: ['entity', id],
    queryFn: () => fetchEntity(id!),
    enabled: !!id,
    staleTime: 60_000,
  });
}

export function useEntityNetwork(id: string | null, depth: number = 2) {
  return useQuery({
    queryKey: ['entity-network', id, depth],
    queryFn: () => fetchEntityNetwork(id!, depth),
    enabled: !!id,
    staleTime: 60_000,
  });
}

export function useSearchEntities(query: string) {
  return useQuery({
    queryKey: ['entity-search', query],
    queryFn: () => searchEntities(query),
    enabled: query.length >= 2,
    staleTime: 10_000,
  });
}

export function useCreateEntity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createEntity,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entities'] });
    },
  });
}

export function useUpdateEntity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateEntity,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['entities'] });
      queryClient.setQueryData(['entity', data.id], data);
    },
  });
}

export function useDeleteEntity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteEntity,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entities'] });
    },
  });
}

export function useCreateEntityFromScan() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createEntityFromScan,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entities'] });
      queryClient.invalidateQueries({ queryKey: ['graph-stats'] });
      queryClient.invalidateQueries({ queryKey: ['full-graph'] });
    },
  });
}

export function useCreateRelationship() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createRelationship,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['entities'] });
      queryClient.invalidateQueries({ queryKey: ['full-graph'] });
    },
  });
}
